import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import Logo from "@/components/Logo";

const COLUMNS = [
  {
    title: "Catalog",
    links: [
      { label: "All Peptides", to: "/peptides" },
      { label: "Pen Kits", to: "/pen-kits" },
      { label: "Raw Vials", to: "/raw-vials" },
      { label: "Bundles", to: "/bundles" },
      { label: "Metabolic", to: "/peptides?category=Metabolic" },
      { label: "Tissue & Cellular", to: "/peptides?category=Tissue+%26+Cellular" },
      { label: "Peptide Blends", to: "/blends" },
    ],
  },
  {
    title: "Trust",
    links: [
      { label: "About", to: "/about" },
      { label: "Authenticity", to: "/authenticity" },
      { label: "Lab testing", to: "/coa" },
      { label: "Verify a Box", to: "/verify" },
      { label: "Research Guides", to: "/learn" },
      { label: "Pay in crypto", to: "/crypto-guide" },
      { label: "Support", to: "/contact" },
      { label: "FAQ", to: "/#faq" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of service", to: "/about" },
      { label: "Privacy policy", to: "/about" },
      { label: "Research disclaimer", to: "/about" },
      { label: "Shipping policy", to: "/about" },
      { label: "Refund policy", to: "/about" },
      { label: "Distribution policy", to: "/about" },
    ],
  },
];

const TAGS = ["UK/EU · FOUNDED 2025", "JANOSHIK-VERIFIED", "TRACKED UK & EU DISPATCH"];

export default function Footer() {
  return (
    <footer className="bg-navy text-white" data-testid="footer">
      <div className="container-pd py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Logo dark />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">
              PepDirect is the authorized distributor of EU-manufactured research compounds by Trenta
              Medical. Every lot is independently tested by Janoshik Analytical: HPLC purity plus a
              separate pH test, two PDFs per lot, tracked UK & EU shipping.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {TAGS.map((t) => (
                <span
                  key={t}
                  className="rounded-sm bg-white/10 px-2 py-1 font-mono text-[10px] tracking-[0.15em] text-white/80"
                  data-testid={`footer-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                >
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-6 rounded-lg border border-white/15 p-4">
              <p className="label-mono !text-white/50">↳ Authorized distributor</p>
              <p className="mt-1 text-sm font-semibold">Manufactured by Trenta Medical</p>
            </div>
          </div>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="label-mono !text-white/50">{col.title}</h4>
              <ul className="mt-4 space-y-2.5 text-sm">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-white/70 transition-colors hover:text-white" data-testid={`footer-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          <div className="lg:col-span-2">
            <h4 className="label-mono !text-white/50">Contact</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <a href="https://t.me/pepdirect" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 font-semibold text-brandbluelight hover:text-white" data-testid="footer-telegram">
                  <Send className="h-3.5 w-3.5" strokeWidth={1.5} /> Telegram
                </a>
              </li>
              <li><a href="mailto:research@pepdirect.co" className="text-white/70 hover:text-white">research@pepdirect.co</a></li>
              <li><a href="mailto:lab@pepdirect.co" className="text-white/70 hover:text-white">lab@pepdirect.co</a></li>
              <li><a href="mailto:shipping@pepdirect.co" className="text-white/70 hover:text-white">shipping@pepdirect.co</a></li>
              <li><a href="mailto:compliance@pepdirect.co" className="text-white/70 hover:text-white">compliance@pepdirect.co</a></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom legal bar */}
      <div className="border-t border-white/10">
        <div className="container-pd flex flex-col gap-3 py-6 lg:flex-row lg:items-start lg:justify-between">
          <p className="max-w-4xl text-xs leading-relaxed text-white/50">
            Research compounds distributed by PepDirect (United Kingdom/EU). All compounds are intended
            exclusively for laboratory research use. Not for human or veterinary consumption,
            not approved by any regulatory body for therapeutic use.
            Researchers must affirm research-use-only at checkout.
          </p>
          <p
            className="shrink-0 font-mono text-[10px] tracking-[0.15em] text-brandbluelight"
            data-testid="footer-research-use-pill"
          >
            FOR RESEARCH USE ONLY · NOT FOR HUMAN CONSUMPTION
          </p>
        </div>
      </div>
    </footer>
  );
}
